import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { Banner, Card, ErrorNote } from '@/components/Common';

interface PlatformSettings {
  // Jackson serialises a BigDecimal as a JSON number, not a string, despite the field's name —
  // normalise with String() wherever this is read as text (it broke the amount field below once
  // already).
  usdToZwgRate: number;
  platformFeeUsd: number;
  zwgAcquisitionRate: number;
}

/** Platform-wide settings an admin can change without a redeploy — currently just the USD
 * exchange rate the amount calculator (vendor till, company recharge, public checkout) uses. */
export function SettingsPage() {
  const queryClient = useQueryClient();
  const [rate, setRate] = useState('');
  const [fee, setFee] = useState('');
  const [acqRate, setAcqRate] = useState('');
  const [saved, setSaved] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['platform-settings'],
    queryFn: () => api.get<PlatformSettings>('/api/admin/settings'),
  });

  const save = useMutation({
    mutationFn: (body: { usdToZwgRate: string; platformFeeUsd: string; zwgAcquisitionRate: string }) =>
      api.patch<PlatformSettings>('/api/admin/settings', body),
    onSuccess: (updated) => {
      queryClient.setQueryData(['platform-settings'], updated);
      queryClient.invalidateQueries({ queryKey: ['fx-rate'] });
      setSaved(true);
    },
  });

  const currentRate = rate || (data ? String(data.usdToZwgRate) : '');
  const rateNum = Number(currentRate);
  const rateValid = currentRate.trim() !== '' && Number.isFinite(rateNum) && rateNum > 0;

  const currentFee = fee || (data ? String(data.platformFeeUsd) : '');
  const feeNum = Number(currentFee);
  const feeValid = currentFee.trim() !== '' && Number.isFinite(feeNum) && feeNum >= 0;

  const currentAcqRate = acqRate || (data ? String(data.zwgAcquisitionRate) : '');
  const acqRateNum = Number(currentAcqRate);
  const acqRateValid = currentAcqRate.trim() !== '' && Number.isFinite(acqRateNum) && acqRateNum > 0;

  const valid = rateValid && feeValid && acqRateValid;
  const unchanged = data
    && currentRate === String(data.usdToZwgRate) && currentFee === String(data.platformFeeUsd)
    && currentAcqRate === String(data.zwgAcquisitionRate);

  return (
    <div className="space-y-4">
      <Card title="USD exchange rate">
        {isLoading && <p className="p-4 text-sm text-ink-500">Loading…</p>}
        {error && <div className="p-4"><ErrorNote error={error} /></div>}
        {data && (
          <div className="space-y-3 p-4">
            <p className="text-sm text-ink-600">
              The rate vendor, company and public checkout screens use to convert a USD figure a
              customer says ("I want $3 of this") into the ZWG amount a product is actually priced
              in. Takes effect immediately everywhere.
            </p>
            <label className="block max-w-xs">
              <span className="label">1 USD =</span>
              <div className="mt-1 flex items-center gap-2">
                <input className="input tabular" inputMode="decimal"
                       value={rate || String(data.usdToZwgRate)}
                       onChange={(e) => { setRate(e.target.value); setSaved(false); }} />
                <span className="text-sm text-ink-500">ZWG</span>
              </div>
            </label>

            <label className="block max-w-xs">
              <span className="label">Platform fee per recharge</span>
              <div className="mt-1 flex items-center gap-2">
                <span className="text-sm text-ink-500">$</span>
                <input className="input tabular" inputMode="decimal"
                       value={fee || String(data.platformFeeUsd)}
                       onChange={(e) => { setFee(e.target.value); setSaved(false); }} />
              </div>
              <span className="mt-1 block text-xs text-ink-500">
                Charged on every vendor and company recharge, on top of the recharge amount — not
                charged to API-integrated merchants.
              </span>
            </label>

            <label className="block max-w-xs">
              <span className="label">ZiG acquisition rate</span>
              <div className="mt-1 flex items-center gap-2">
                <span className="text-sm text-ink-500">1 USD =</span>
                <input className="input tabular" inputMode="decimal"
                       value={acqRate || String(data.zwgAcquisitionRate)}
                       onChange={(e) => { setAcqRate(e.target.value); setSaved(false); }} />
                <span className="text-sm text-ink-500">ZWG</span>
              </div>
              <span className="mt-1 block text-xs text-ink-500">
                The rate ZiG is actually bought at (street/bureau). HOT pays zero commission on
                ZiG airtime — the gap between this and the rate above, on every ZWG recharge, is
                the platform's real earning on that side.
              </span>
            </label>

            {save.isError && <ErrorNote error={save.error} />}
            {saved && <Banner tone="info">Saved — the new settings are live.</Banner>}
            <button type="button" className="btn-primary"
                    disabled={!valid || save.isPending || unchanged}
                    onClick={() => save.mutate({
                      usdToZwgRate: currentRate, platformFeeUsd: currentFee,
                      zwgAcquisitionRate: currentAcqRate,
                    })}>
              {save.isPending ? 'Saving…' : 'Save settings'}
            </button>
          </div>
        )}
      </Card>
    </div>
  );
}
